// components/modals/SkillEditor.jsx
//
// Full manual editor for an Agent Skill folder (agentskills.io format, the
// same layout as github.com/anthropics/skills and github.com/openai/skills):
//
//   SKILL.md            -> edited as a form (name, description, instructions,
//                          allowed tools, license, compatibility, metadata)
//   references/ scripts/ assets/ ...  -> bundled files, added/edited here
//
// Create: everything stays in memory, then SKILL.md + files are sent to
// POST /data/skills/import so the backend parses/validates them exactly like
// an uploaded folder. Edit: fields via PATCH, files via PUT/DELETE
// /data/skills/{id}/files. System/built-in skills open read-only.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiDownload,
  FiFile,
  FiFileText,
  FiFolder,
  FiLoader,
  FiLock,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiX,
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import Modal from '../base/Modal';
import * as skillsApi from '../../services/skillsApi';
import { ToolPicker, errorMessage, formatBytes, slugify } from './skillsShared';

const SKILL_MD = 'SKILL.md';
const FOLDERS = ['references', 'scripts', 'assets', 'examples', 'templates'];
const DESCRIPTION_MAX = 1024;
const COMPATIBILITY_MAX = 500;
// Mirrors the backend's text allow-list (app/services/skills/package.py).
const TEXT_EXTENSIONS = new Set(
  'md markdown txt rst py js mjs cjs ts tsx jsx sh bash ps1 rb go java cs php swift sql json jsonl yaml yml toml ini cfg xml xsd svg csv tsv html htm css ipynb'.split(
    ' ',
  ),
);
const BARE_TEXT_NAMES = new Set(['LICENSE', 'NOTICE', 'COPYING']);
// Binary types a skill may bundle (same list as the backend).
const BINARY_EXTENSIONS = new Set(
  'png jpg jpeg gif webp ico pdf ttf otf woff woff2 docx xlsx pptx gz tgz tar zip'.split(
    ' ',
  ),
);
const ALLOWED_TYPES_HINT =
  'text or code (.md, .txt, .py, .js, .json, .yaml …), images, PDFs, fonts, Office documents or archives';

const NEW_SKILL_INSTRUCTIONS = `# My Skill

One or two sentences on what this skill is for.

## Steps

1. ...
2. ...

## Rules

- Mention every bundled file (e.g. \`references/guide.md\`) and when to open it.
`;

function isTextPath(path) {
  const name = path.split('/').pop();
  if (BARE_TEXT_NAMES.has(name.toUpperCase())) return true;
  const dot = name.lastIndexOf('.');
  return dot > 0 && TEXT_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

function extensionOf(path) {
  const name = path.split('/').pop();
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

// Extension added to a new file typed without one, by folder.
const DEFAULT_EXTENSION = { scripts: 'py', assets: 'txt' };

// The path a typed name becomes: folder prefix + a default extension when
// the name has none ("a" in scripts/ -> "scripts/a.py", elsewhere "a.md").
function newFilePath(folder, rawName) {
  const name = rawName.trim().replace(/^\/+/, '');
  if (!name) return '';
  const withExt = extensionOf(name)
    ? name
    : `${name}.${DEFAULT_EXTENSION[folder] || 'md'}`;
  return folder ? `${folder}/${withExt}` : withExt;
}

// Anything else is dropped by the server, so refuse it here instead of
// letting a file silently disappear on save.
function isAllowedPath(path) {
  return isTextPath(path) || BINARY_EXTENSIONS.has(extensionOf(path));
}

// agents/*.yaml is UI metadata managed by the platform, not edited here.
function isManagedPath(path) {
  return (
    path === SKILL_MD || (path.startsWith('agents/') && /\.ya?ml$/.test(path))
  );
}

function validRelativePath(path) {
  return (
    path &&
    !path.startsWith('/') &&
    !path.split('/').some((seg) => seg === '..' || seg === '' || seg === '.')
  );
}

// JSON strings are valid YAML scalars, so this quotes anything safely.
const q = (v) => JSON.stringify(String(v));

function buildSkillMd(meta) {
  const lines = [
    '---',
    `name: ${q(meta.slug)}`,
    `description: ${q(meta.description)}`,
  ];
  if (meta.license.trim()) lines.push(`license: ${q(meta.license.trim())}`);
  if (meta.compatibility.trim())
    lines.push(`compatibility: ${q(meta.compatibility.trim())}`);
  if (meta.toolNames.length)
    lines.push(`allowed-tools: ${q(meta.toolNames.join(' '))}`);
  const md = meta.metadata.filter((r) => r.key.trim());
  if (md.length) {
    lines.push('metadata:');
    md.forEach((r) => lines.push(`  ${q(r.key.trim())}: ${q(r.value)}`));
  }
  lines.push('---', '', meta.instructions.trim(), '');
  return lines.join('\n');
}

function metaFromSkill(skill) {
  return {
    name: skill?.name || '',
    slug: skill?.slug || '',
    description: skill?.description || '',
    instructions: skill ? skill.instructions || '' : NEW_SKILL_INSTRUCTIONS,
    toolNames: skill?.tool_names || [],
    license: skill?.license || '',
    compatibility: skill?.compatibility || '',
    metadata: Object.entries(skill?.metadata || {}).map(([key, value]) => ({
      key,
      value,
    })),
  };
}

function filesFromSkill(skill) {
  const files = {};
  (skill?.files || []).forEach((f) => {
    if (f.path === SKILL_MD) return;
    files[f.path] = {
      path: f.path,
      isText: f.is_text,
      size: f.size,
      content: null,
      blob: null,
      origin: 'existing',
      modified: false,
      loaded: false,
      truncated: false,
      managed: isManagedPath(f.path),
    };
  });
  return files;
}

function sameMeta(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

const inputClass =
  'mt-1 w-full rounded-md border border-zinc-700 bg-zinc-900 p-2 text-sm text-zinc-200 outline-none focus:border-[#5652CF] focus:ring-2 focus:ring-[#5652CF] disabled:cursor-not-allowed disabled:opacity-70';
const labelClass = 'text-xs font-medium text-zinc-400';

function FileTree({ files, selected, onSelect, onDelete, readOnly }) {
  const groups = useMemo(() => {
    const byFolder = {};
    Object.values(files).forEach((f) => {
      const folder = f.path.includes('/') ? f.path.split('/')[0] : '';
      (byFolder[folder] = byFolder[folder] || []).push(f);
    });
    Object.values(byFolder).forEach((list) =>
      list.sort((a, b) => a.path.localeCompare(b.path)),
    );
    return Object.entries(byFolder).sort(([a], [b]) =>
      a === '' ? -1 : b === '' ? 1 : a.localeCompare(b),
    );
  }, [files]);

  const row = (path, label, icon, extra) => (
    <div
      key={path}
      className={`group flex items-center justify-between rounded px-2 py-1 text-xs ${
        selected === path
          ? 'bg-[#5652CF]/20 text-white'
          : 'text-zinc-300 hover:bg-zinc-800'
      }`}
    >
      <button
        type="button"
        onClick={() => onSelect(path)}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
      >
        {icon}
        <span className="truncate font-mono">{label}</span>
      </button>
      {extra}
    </div>
  );

  return (
    <div className="space-y-2">
      {row(
        SKILL_MD,
        SKILL_MD,
        <FiFileText className="h-3 w-3 flex-shrink-0 text-[#8b88f0]" />,
      )}
      {groups.map(([folder, list]) => (
        <div key={folder || '(root)'}>
          {folder && (
            <p className="mb-0.5 flex items-center gap-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              <FiFolder className="h-3 w-3" /> {folder}/
            </p>
          )}
          {list.map((f) =>
            row(
              f.path,
              folder ? f.path.slice(folder.length + 1) : f.path,
              f.isText ? (
                <FiFileText className="h-3 w-3 flex-shrink-0 text-zinc-500" />
              ) : (
                <FiFile className="h-3 w-3 flex-shrink-0 text-zinc-500" />
              ),
              <span className="flex items-center gap-1">
                {(f.origin === 'new' || f.modified) && (
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-amber-400"
                    title="Unsaved"
                  />
                )}
                {!readOnly && !f.managed && (
                  <button
                    type="button"
                    onClick={() => onDelete(f.path)}
                    className="rounded p-0.5 text-zinc-500 opacity-0 hover:text-red-400 group-hover:opacity-100"
                    title="Delete file"
                  >
                    <FiTrash2 className="h-3 w-3" />
                  </button>
                )}
              </span>,
            ),
          )}
        </div>
      ))}
    </div>
  );
}

function SkillMdForm({ meta, setMeta, isCreate, readOnly, availableTools }) {
  const set = (patch) => setMeta((m) => ({ ...m, ...patch }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="skill-name" className={labelClass}>
            Name
          </label>
          <input
            id="skill-name"
            className={inputClass}
            value={meta.name}
            disabled={readOnly}
            onChange={(e) =>
              set(
                isCreate
                  ? { name: e.target.value, slug: slugify(e.target.value) }
                  : { name: e.target.value },
              )
            }
            placeholder="e.g. Meeting Minutes"
          />
        </div>
        <div>
          <span className={labelClass}>Skill ID</span>
          <p className="mt-1 truncate rounded-md border border-zinc-800 bg-zinc-950 p-2 font-mono text-sm text-zinc-400">
            {meta.slug || '—'}
          </p>
          <p className="mt-1 text-[10px] text-zinc-600">
            {isCreate
              ? 'From the name: lowercase letters, numbers and hyphens.'
              : "Can't be changed after creation."}
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <label htmlFor="skill-description" className={labelClass}>
            Description
          </label>
          <span
            className={`text-[10px] ${
              meta.description.length > DESCRIPTION_MAX
                ? 'text-red-400'
                : 'text-zinc-600'
            }`}
          >
            {meta.description.length}/{DESCRIPTION_MAX}
          </span>
        </div>
        <textarea
          id="skill-description"
          rows={3}
          className={inputClass}
          value={meta.description}
          disabled={readOnly}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="What the skill does AND when to use it, e.g. “Turns meeting notes into minutes. Use when the user shares notes or a transcript.”"
        />
        <p className="mt-1 text-[10px] text-zinc-600">
          The assistant only sees this line until it decides to load the skill,
          so say when it applies.
        </p>
      </div>

      <div>
        <label htmlFor="skill-instructions" className={labelClass}>
          Instructions (Markdown)
        </label>
        <textarea
          id="skill-instructions"
          rows={14}
          className={`${inputClass} font-mono text-xs leading-relaxed`}
          value={meta.instructions}
          disabled={readOnly}
          onChange={(e) => set({ instructions: e.target.value })}
        />
      </div>

      <div>
        <label htmlFor="skill-tools" className={labelClass}>
          Allowed tools
        </label>
        <div className="mt-1">
          {readOnly ? (
            <p className="text-xs text-zinc-400">
              {meta.toolNames.length ? meta.toolNames.join(', ') : 'None'}
            </p>
          ) : (
            <ToolPicker
              id="skill-tools"
              availableTools={availableTools}
              selected={meta.toolNames}
              onChange={(toolNames) => set({ toolNames })}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="skill-license" className={labelClass}>
            License <span className="text-zinc-600">(optional)</span>
          </label>
          <input
            id="skill-license"
            className={inputClass}
            value={meta.license}
            disabled={readOnly}
            onChange={(e) => set({ license: e.target.value })}
            placeholder="e.g. Proprietary, MIT"
          />
        </div>
        <div>
          <label htmlFor="skill-compat" className={labelClass}>
            Compatibility <span className="text-zinc-600">(optional)</span>
          </label>
          <input
            id="skill-compat"
            className={inputClass}
            value={meta.compatibility}
            maxLength={COMPATIBILITY_MAX}
            disabled={readOnly}
            onChange={(e) => set({ compatibility: e.target.value })}
            placeholder="Only if it needs something special, e.g. Requires internet access"
          />
        </div>
      </div>

      <div>
        <span className={labelClass}>
          Metadata <span className="text-zinc-600">(optional)</span>
        </span>
        <div className="mt-1 space-y-2">
          {meta.metadata.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="flex gap-2">
              <input
                className={`${inputClass} mt-0`}
                value={row.key}
                disabled={readOnly}
                placeholder="key (e.g. version)"
                onChange={(e) =>
                  set({
                    metadata: meta.metadata.map((r, j) =>
                      j === i ? { ...r, key: e.target.value } : r,
                    ),
                  })
                }
              />
              <input
                className={`${inputClass} mt-0`}
                value={row.value}
                disabled={readOnly}
                placeholder="value (e.g. 1.0.0)"
                onChange={(e) =>
                  set({
                    metadata: meta.metadata.map((r, j) =>
                      j === i ? { ...r, value: e.target.value } : r,
                    ),
                  })
                }
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() =>
                    set({ metadata: meta.metadata.filter((_, j) => j !== i) })
                  }
                  className="rounded px-2 text-zinc-500 hover:text-red-400"
                  title="Remove"
                >
                  <FiX />
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button
              type="button"
              onClick={() =>
                set({ metadata: [...meta.metadata, { key: '', value: '' }] })
              }
              className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white"
            >
              <FiPlus className="h-3 w-3" /> Add field
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const SkillEditor = ({
  isOpen,
  mode, // 'create' | 'edit' | 'view'
  skill,
  availableTools,
  accessToken,
  onClose,
  onSaved,
}) => {
  const isCreate = mode === 'create';
  const readOnly = mode === 'view';

  const [meta, setMeta] = useState(metaFromSkill(null));
  const [initialMeta, setInitialMeta] = useState(metaFromSkill(null));
  const [files, setFiles] = useState({});
  const [deleted, setDeleted] = useState([]);
  const [selected, setSelected] = useState(SKILL_MD);
  const [newFolder, setNewFolder] = useState('references');
  const [newName, setNewName] = useState('');
  const [addingFile, setAddingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const uploadRef = useRef(null);
  const replaceRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const m = metaFromSkill(isCreate ? null : skill);
    setMeta(m);
    setInitialMeta(m);
    setFiles(isCreate ? {} : filesFromSkill(skill));
    setDeleted([]);
    setSelected(SKILL_MD);
    setAddingFile(false);
    setNewName('');
    setConfirmDiscard(false);
  }, [isOpen, isCreate, skill]);

  const current = selected === SKILL_MD ? null : files[selected];

  // Lazy-load text content of existing files when opened.
  useEffect(() => {
    if (!current || current.origin !== 'existing' || current.loaded) return;
    if (!current.isText) return;
    let cancelled = false;
    skillsApi
      .getSkillFileContent(accessToken, skill.id, current.path)
      .then(({ data }) => {
        if (cancelled) return;
        setFiles((fs) => ({
          ...fs,
          [current.path]: {
            ...fs[current.path],
            content: data.content ?? '',
            truncated: !!data.truncated,
            loaded: true,
          },
        }));
      })
      .catch((err) => toast.error(errorMessage(err, 'Could not load file.')));
    return () => {
      cancelled = true;
    };
  }, [current, accessToken, skill]);

  const dirty =
    !sameMeta(meta, initialMeta) ||
    deleted.length > 0 ||
    Object.values(files).some((f) => f.origin === 'new' || f.modified);

  const invalidReason = (() => {
    if (!meta.slug) return 'Give the skill a name.';
    if (!meta.description.trim()) return 'Add a description.';
    if (meta.description.length > DESCRIPTION_MAX)
      return `Description is over ${DESCRIPTION_MAX} characters.`;
    if (!meta.instructions.trim()) return 'Add instructions.';
    return null;
  })();

  const requestClose = () => {
    if (dirty && !readOnly && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const addFile = () => {
    const name = newName.trim().replace(/^\/+/, '');
    const path = newFilePath(newFolder, newName);
    if (!name || !validRelativePath(path)) {
      toast.error('Enter a file name without “..” or a leading slash.');
      return;
    }
    if (files[path] || path === SKILL_MD) {
      toast.error(`${path} already exists.`);
      return;
    }
    if (isManagedPath(path)) {
      toast.error(`${path} is managed automatically.`);
      return;
    }
    if (!isTextPath(path)) {
      toast.error(
        `New files must be text or code (.md, .txt, .py, .js, .json …). Use Upload for “${path.split('/').pop()}”.`,
      );
      return;
    }
    setFiles((fs) => ({
      ...fs,
      [path]: {
        path,
        isText: true,
        size: 0,
        content: '',
        blob: null,
        origin: 'new',
        modified: false,
        loaded: true,
        truncated: false,
        managed: false,
      },
    }));
    setDeleted((d) => d.filter((p) => p !== path));
    setSelected(path);
    setNewName('');
    setAddingFile(false);
  };

  const uploadFiles = async (fileList) => {
    const added = {};
    const skipped = [];
    for (const file of Array.from(fileList || [])) {
      const path = newFolder ? `${newFolder}/${file.name}` : file.name;
      if (isManagedPath(path)) continue;
      if (!isAllowedPath(path)) {
        skipped.push(file.name);
        continue;
      }
      const isText = isTextPath(path);
      added[path] = {
        path,
        isText,
        size: file.size,
        content: isText ? await file.text() : null,
        blob: isText ? null : file,
        origin: files[path]?.origin === 'existing' ? 'existing' : 'new',
        modified: true,
        loaded: true,
        truncated: false,
        managed: false,
      };
    }
    if (skipped.length) {
      toast.error(
        `Skipped ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? ` and ${skipped.length - 3} more` : ''}: skills can only bundle ${ALLOWED_TYPES_HINT}.`,
      );
    }
    const paths = Object.keys(added);
    if (!paths.length) return;
    setFiles((fs) => ({ ...fs, ...added }));
    setDeleted((d) => d.filter((p) => !added[p]));
    setSelected(paths[0]);
  };

  const replaceBinary = (file) => {
    if (!file || !current) return;
    setFiles((fs) => ({
      ...fs,
      [current.path]: {
        ...fs[current.path],
        blob: file,
        size: file.size,
        modified: true,
      },
    }));
  };

  const deleteFile = (path) => {
    const f = files[path];
    if (!f) return;
    setFiles((fs) => {
      const next = { ...fs };
      delete next[path];
      return next;
    });
    if (f.origin === 'existing') setDeleted((d) => [...d, path]);
    if (selected === path) setSelected(SKILL_MD);
  };

  const fileBlob = (f) =>
    f.isText
      ? new Blob([f.content ?? ''], { type: 'text/plain' })
      : f.blob || new Blob([]);

  const handleCreate = async () => {
    const entries = [
      {
        path: `${meta.slug}/${SKILL_MD}`,
        blob: new Blob([buildSkillMd(meta)], { type: 'text/markdown' }),
      },
      ...Object.values(files).map((f) => ({
        path: `${meta.slug}/${f.path}`,
        blob: fileBlob(f),
      })),
    ];
    let { data: created } = await skillsApi.importSkillEntries(
      accessToken,
      entries,
    );
    if (meta.name.trim() && meta.name.trim() !== created.name) {
      ({ data: created } = await skillsApi.updateSkill(
        accessToken,
        created.id,
        {
          name: meta.name.trim(),
        },
      ));
    }
    return created;
  };

  const handleUpdate = async () => {
    let updated = skill;
    if (!sameMeta(meta, initialMeta)) {
      ({ data: updated } = await skillsApi.updateSkill(accessToken, skill.id, {
        name: meta.name,
        description: meta.description,
        instructions: meta.instructions,
        toolNames: meta.toolNames,
        license: meta.license,
        compatibility: meta.compatibility,
        metadata: Object.fromEntries(
          meta.metadata
            .filter((r) => r.key.trim())
            .map((r) => [r.key.trim(), r.value]),
        ),
      }));
    }
    const changed = Object.values(files).filter(
      (f) => f.origin === 'new' || f.modified,
    );
    if (changed.length) {
      ({ data: updated } = await skillsApi.putSkillFiles(
        accessToken,
        skill.id,
        changed.map((f) => ({ path: f.path, blob: fileBlob(f) })),
      ));
    }
    for (const path of deleted) {
      ({ data: updated } = await skillsApi.deleteSkillFile(
        accessToken,
        skill.id,
        path,
      ));
    }
    return updated;
  };

  const handleSave = async () => {
    if (invalidReason) {
      toast.error(invalidReason);
      return;
    }
    setSaving(true);
    try {
      const result = isCreate ? await handleCreate() : await handleUpdate();
      const notes = result?.import_warnings?.length || 0;
      toast.success(
        isCreate
          ? `Skill created${notes ? ` with ${notes} note${notes > 1 ? 's' : ''}` : ''}.`
          : 'Skill saved.',
      );
      onSaved(result, { created: isCreate });
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to save skill.'));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      await skillsApi.exportSkill(accessToken, skill.id, skill.slug);
    } catch (err) {
      toast.error(errorMessage(err, 'Export failed.'));
    }
  };

  const title = isCreate
    ? 'New Skill'
    : `${readOnly ? 'View' : 'Edit'} Skill — ${skill?.name || ''}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      title={title}
      desc="A skill is a folder: SKILL.md (instructions) plus optional references, scripts and assets."
    >
      <div className="flex h-[70vh] w-[min(1100px,calc(100vw-4rem))] flex-col">
        <div className="flex min-h-0 flex-1">
          {/* File tree */}
          <aside className="flex w-60 flex-shrink-0 flex-col border-r border-zinc-800 p-3">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <FileTree
                files={files}
                selected={selected}
                onSelect={setSelected}
                onDelete={deleteFile}
                readOnly={readOnly}
              />
            </div>
            {!readOnly && (
              <div className="mt-3 space-y-2 border-t border-zinc-800 pt-3">
                <select
                  value={newFolder}
                  onChange={(e) => setNewFolder(e.target.value)}
                  className="w-full rounded border border-zinc-700 bg-zinc-900 p-1.5 text-xs text-zinc-200"
                  aria-label="Folder for new files"
                >
                  {FOLDERS.map((f) => (
                    <option key={f} value={f}>
                      {f}/
                    </option>
                  ))}
                  <option value="">(skill root)</option>
                </select>
                {addingFile ? (
                  <div>
                    <div className="flex gap-1">
                      <input
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') addFile();
                          if (e.key === 'Escape') setAddingFile(false);
                        }}
                        placeholder={`file-name.${DEFAULT_EXTENSION[newFolder] || 'md'}`}
                        className="min-w-0 flex-1 rounded border border-zinc-700 bg-zinc-900 p-1.5 font-mono text-xs text-zinc-200"
                      />
                      <button
                        type="button"
                        onClick={addFile}
                        className="rounded bg-[#5652CF] px-2 text-xs text-white"
                      >
                        Add
                      </button>
                    </div>
                    {newName.trim() && (
                      <p className="mt-1 truncate text-[10px] text-zinc-500">
                        Will be created as{' '}
                        <span className="font-mono text-zinc-300">
                          {newFilePath(newFolder, newName)}
                        </span>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setAddingFile(true)}
                      className="flex flex-1 items-center justify-center gap-1 rounded border border-zinc-700 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 hover:text-white"
                    >
                      <FiPlus className="h-3 w-3" /> New file
                    </button>
                    <button
                      type="button"
                      onClick={() => uploadRef.current?.click()}
                      className="flex flex-1 items-center justify-center gap-1 rounded border border-zinc-700 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 hover:text-white"
                    >
                      <FiUpload className="h-3 w-3" /> Upload
                    </button>
                  </div>
                )}
                <input
                  ref={uploadRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    uploadFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>
            )}
          </aside>

          {/* Editor pane */}
          <section className="min-w-0 flex-1 overflow-y-auto p-4">
            {selected === SKILL_MD ? (
              <SkillMdForm
                meta={meta}
                setMeta={setMeta}
                isCreate={isCreate}
                readOnly={readOnly}
                availableTools={availableTools}
              />
            ) : current ? (
              <div className="flex h-full flex-col">
                <div className="mb-2 flex items-center justify-between">
                  <p className="truncate font-mono text-xs text-zinc-300">
                    {current.path}
                  </p>
                  <span className="text-[10px] text-zinc-600">
                    {formatBytes(
                      current.isText
                        ? (current.content ?? '').length
                        : current.size,
                    )}
                  </span>
                </div>
                {current.managed ? (
                  <p className="flex items-start gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 p-3 text-xs text-zinc-400">
                    <FiLock className="mt-0.5 h-3 w-3 flex-shrink-0" />
                    Display metadata for other tools. It isn't used by the
                    assistant and can't be edited here.
                  </p>
                ) : current.isText ? (
                  !current.loaded ? (
                    <div className="flex h-32 items-center justify-center">
                      <FiLoader className="animate-spin text-[#5652CF]" />
                    </div>
                  ) : (
                    <>
                      {current.truncated && (
                        <p className="mb-2 flex items-center gap-1.5 text-[11px] text-amber-400">
                          <FiAlertTriangle className="h-3 w-3" /> Too large to
                          edit here (over 200 KB). Upload a replacement instead.
                        </p>
                      )}
                      <textarea
                        className="min-h-0 w-full flex-1 resize-none rounded-md border border-zinc-700 bg-zinc-950 p-3 font-mono text-xs leading-relaxed text-zinc-200 outline-none focus:border-[#5652CF] disabled:opacity-70"
                        value={current.content ?? ''}
                        disabled={readOnly || current.truncated}
                        spellCheck={false}
                        onChange={(e) =>
                          setFiles((fs) => ({
                            ...fs,
                            [current.path]: {
                              ...fs[current.path],
                              content: e.target.value,
                              modified: fs[current.path].origin === 'existing',
                            },
                          }))
                        }
                      />
                    </>
                  )
                ) : (
                  <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-900/60 p-4 text-xs text-zinc-400">
                    <p>
                      Binary file ({formatBytes(current.size)}). It's bundled
                      with the skill as-is.
                    </p>
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          onClick={() => replaceRef.current?.click()}
                          className="flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1.5 text-zinc-300 hover:text-white"
                        >
                          <FiUpload className="h-3 w-3" /> Replace file
                        </button>
                        <input
                          ref={replaceRef}
                          type="file"
                          className="hidden"
                          onChange={(e) => {
                            replaceBinary(e.target.files?.[0]);
                            e.target.value = '';
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between gap-3 border-t border-zinc-800 px-4 py-3">
          <div className="min-w-0 text-[11px]">
            {confirmDiscard ? (
              <span className="text-amber-400">
                Discard unsaved changes?{' '}
                <button
                  type="button"
                  onClick={() => setConfirmDiscard(false)}
                  className="ml-2 underline hover:text-white"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="ml-2 font-semibold underline hover:text-white"
                >
                  Discard
                </button>
              </span>
            ) : readOnly ? (
              <span className="flex items-center gap-1 text-zinc-500">
                <FiLock className="h-3 w-3" /> System skill — read-only.
              </span>
            ) : dirty ? (
              <span className="text-amber-400">Unsaved changes</span>
            ) : (
              <span className="text-zinc-600">No changes</span>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {!isCreate && (
              <button
                type="button"
                onClick={handleExport}
                className="flex items-center gap-1 rounded-md px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800"
              >
                <FiDownload className="h-3 w-3" /> Export .zip
              </button>
            )}
            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              className="rounded-md px-4 py-2 text-xs text-zinc-300 hover:bg-zinc-700 disabled:opacity-50"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || (!isCreate && !dirty)}
                title={invalidReason || undefined}
                className="flex h-8 w-32 items-center justify-center rounded-md bg-[#5652CF] px-4 text-xs font-semibold text-white hover:bg-[#4642bf] disabled:cursor-not-allowed disabled:bg-zinc-600"
              >
                {saving ? (
                  <FiLoader className="animate-spin" />
                ) : isCreate ? (
                  'Create Skill'
                ) : (
                  'Save Changes'
                )}
              </button>
            )}
          </div>
        </footer>
      </div>
    </Modal>
  );
};

export default SkillEditor;
